import { age } from "./body";
import { isFastDay } from "./dayMarks";
import type { EatingCondition, EatingMed, FoodEntry, KayaState } from "./types";

/**
 * What age, the end of periods and the questionnaire's answers change about a day's targets.
 * (ADR-039)
 *
 * `nutrition.ts::dailyTargets` and `intake.ts::deficitTarget` / `proteinTarget` read these rules;
 * nothing else decides a deficit, a protein rate or an iron figure. Each rule returns the value
 * *and* why it applies, so the "How this is worked out" lines can name the rule that bit instead
 * of describing the defaults to someone they no longer fit.
 *
 * Sources (docs/research/2026-09-25/questionnaire-and-planning.md §2.3, §2.7, §4.1):
 * - Protein at 60+: PROT-AGE, 1.0–1.2 g/kg for healthy older people and 25–30 g a meal; ESPEN's
 *   floor is 1 g/kg. Adults keep 1.6 (training in a deficit) and a 20 g per-meal floor.
 * - Deficit at 60+: ESPEN/EASO, about 500 kcal a day at most — more costs muscle (sarcopenia).
 * - Diabetes: ADA Standards 2025 §13 — no aggressive deficit; no skipped meals on a drug that can
 *   cause low sugar.
 * - Kidney disease: DGI 2024 — high-protein diets need care in renal disease; protein is held at
 *   the adult allowance (ICMR-NIN 2020, 0.83 g/kg) and the doctor sets anything else.
 * - Pregnancy and breastfeeding: DGI 2024 adds energy and protein — no deficit.
 * - Iron: ICMR-NIN 2020's 29 mg for women is the menstruating years' figure; DGI 2024's
 *   older-woman diet gives ~22 mg. Once periods stop, or from 50, the adult 19 mg applies.
 * - Calcium: ICMR-NIN 2020's adult 1,000 mg. A 1,200 mg figure for 60+ exists only in a secondary
 *   summary, not verified against the RDA book, so it is not shown.
 * - Hydration: ESPEN — at least 1.6 L of drinks a day for older women, 2.0 L for men.
 * - A GLP-1 or other weight-loss medicine (plan §9 row 10; market-gaps.md §5): appetite is already
 *   low, so the risk is losing muscle — protein at least 1.2 g/kg and the deficit never raised past
 *   the usual 500. Kaya never touches the dose. (ADR-042)
 * - The optional safety question (questionnaire-and-planning.md §4.1 row 16): a "Yes" switches the
 *   deficit off. The answer never leaves the phone (ADR-020: admins can read an account), so it
 *   reaches these rules as `DeviceAnswers`, not through the state. (ADR-042)
 */

export const DEFAULT_DEFICIT_KCAL = 500;
export const DEFAULT_PROTEIN_PER_KG = 1.6;

export const OLDER_AGE = 60;
/** "Starts gentle": the default pace after 60, well inside the cap. */
export const OLDER_DEFAULT_DEFICIT_KCAL = 300;
export const OLDER_MAX_DEFICIT_KCAL = 500;
export const DIABETES_MAX_DEFICIT_KCAL = 500;
export const OLDER_PROTEIN_PER_KG = { min: 1.0, max: 1.2, default: 1.2 } as const;
export const KIDNEY_PROTEIN_PER_KG = 0.8;
export const MEAL_PROTEIN_FLOOR_G = { adult: 20, older: 25 } as const;
/** From here a woman's iron target is the adult figure whether or not periods have stopped. */
export const IRON_ADULT_AGE = 50;
export const IRON_MG = { male: 19, female: 29 } as const;
export const GLP1_PROTEIN_PER_KG = 1.2;
export const GLP1_MAX_DEFICIT_KCAL = 500;
export const HYDRATION_L = { male: 2.0, female: 1.6 } as const;

export type AgeBand = "adult" | "60+";

/** Null without a year of birth: the band is a fact about the person, not a guess. */
export function ageBand(S: KayaState, today = new Date()): AgeBand | null {
  const a = age(S, today);
  if (a === null) return null;
  return a >= OLDER_AGE ? "60+" : "adult";
}

export function older(S: KayaState, today = new Date()): boolean {
  return ageBand(S, today) === "60+";
}

/** Only a woman's answer counts: the switch is offered to no one else. */
export function periodsStopped(S: KayaState): boolean {
  return S.profile.sex === "female" && S.profile.periodsStopped === true;
}

export function hasCondition(S: KayaState, c: EatingCondition): boolean {
  return (S.profile.eating?.conditions ?? []).includes(c);
}

export function pregnancy(S: KayaState): "pregnant" | "breastfeeding" | null {
  const p = S.profile.eating?.pregnant;
  return p === "pregnant" || p === "breastfeeding" ? p : null;
}

export function takesMed(S: KayaState, m: EatingMed): boolean {
  return (S.profile.eating?.meds ?? []).includes(m);
}

/**
 * What this phone knows about the person that the synced blob must not carry: the answer to the
 * questionnaire's optional safety question, kept in `LocalMeta` (ADR-042). Screens read it with
 * `store/kayaStore.ts::useDeviceAnswers` and pass it to the rules that decide a budget; a rule
 * called without it simply has nothing to say about it.
 */
export interface DeviceAnswers {
  /** "Yes" to the safety question: no deficit on this phone until the answer changes */
  gentle?: boolean;
  /** A yes on the exercise safety screens (LocalMeta.trainingSafety): the plan is held at easy
   *  walks and the gentle sessions, with no tests, until the answer changes (ADR-053) */
  trainingGate?: boolean;
}

/** No skipped meals: at 60+ (the research's older-adult plan) and on a low-sugar-risk drug. */
export function noSkippedMeals(S: KayaState, today = new Date()): boolean {
  return older(S, today) || S.profile.eating?.hypoRisk === true;
}

/* ---------- deficit ---------- */

function storedDeficit(S: KayaState): number | null {
  const n = S.profile.deficitKcal;
  return typeof n === "number" && Number.isFinite(n) && n >= 0 ? Math.round(n) : null;
}

export function defaultDeficit(S: KayaState, today = new Date()): number {
  return older(S, today) ? OLDER_DEFAULT_DEFICIT_KCAL : DEFAULT_DEFICIT_KCAL;
}

/** The most the settings box may hold: the age cap is a setting's limit, a condition's cap is not
 *  (it lifts when the answer changes, and the setting should still be there). */
export function maxDeficitSetting(S: KayaState, today = new Date()): number {
  return older(S, today) ? OLDER_MAX_DEFICIT_KCAL : 1200;
}

/** What the settings box shows: the saved figure (or the age's default), within the age's cap. */
export function deficitSetting(S: KayaState, today = new Date()): number {
  const n = storedDeficit(S) ?? defaultDeficit(S, today);
  return Math.min(n, maxDeficitSetting(S, today));
}

/** A deficit in one word, for the pace row: "Gentle" is the after-60 default and anything up to it. */
export function paceWord(deficitKcal: number): "Holding steady" | "Gentle" | "Moderate" | "Brisk" {
  if (deficitKcal <= 0) return "Holding steady";
  if (deficitKcal <= OLDER_DEFAULT_DEFICIT_KCAL) return "Gentle";
  if (deficitKcal <= OLDER_MAX_DEFICIT_KCAL) return "Moderate";
  return "Brisk";
}

/** `fast` is a day the person marked as a fast (ADR-049); the others are about the person. */
export type DeficitReason = "pregnant" | "breastfeeding" | "gentle" | "60+" | "diabetes" | "glp1" | "fast";

export interface DeficitRule {
  kcal: number;
  /** what was asked for, before any cap */
  asked: number;
  /** every rule that limits this person's deficit, whether or not it bit today */
  limits: DeficitReason[];
  /** true when a limit lowered what was asked */
  capped: boolean;
}

/**
 * The deficit in force. `device` carries the answers that never sync (ADR-042). Without `ds` it is
 * the person's — what the settings show. With `ds` it is that day's: a day marked as a fast carries
 * none (`fastDayRule`), so every budget built for a day — Food's hero, the ledger, the planner —
 * passes the day it is for.
 */
export function deficitRule(S: KayaState, today = new Date(), device?: DeviceAnswers, ds?: string): DeficitRule {
  const asked = storedDeficit(S) ?? defaultDeficit(S, today);
  const preg = pregnancy(S);
  if (preg) return { kcal: 0, asked, limits: [preg], capped: asked > 0 };
  if (device?.gentle) return { kcal: 0, asked, limits: ["gentle"], capped: asked > 0 };
  const limits: DeficitReason[] = [];
  let kcal = asked;
  if (older(S, today)) {
    limits.push("60+");
    kcal = Math.min(kcal, OLDER_MAX_DEFICIT_KCAL);
  }
  if (hasCondition(S, "diabetes")) {
    limits.push("diabetes");
    kcal = Math.min(kcal, DIABETES_MAX_DEFICIT_KCAL);
  }
  if (takesMed(S, "glp1")) {
    limits.push("glp1");
    kcal = Math.min(kcal, GLP1_MAX_DEFICIT_KCAL);
  }
  if (ds !== undefined && isFastDay(S, ds)) return { kcal: 0, asked, limits: ["fast", ...limits], capped: asked > 0 };
  return { kcal, asked, limits, capped: kcal < asked };
}

/* ---------- a fast day (ADR-049) ---------- */

export interface FastDayRule {
  /** always 0: the fast is the day's gap, and a planned deficit on top of it is a second one */
  deficit: 0;
  /** at 60+ or on a drug that can bring sugar low, the fast is kept as meals of fast-day food,
   *  never as a skipped meal (ADR-039's rule, which a fast does not suspend) */
  keepMeals: boolean;
  why: "60+" | "low sugar" | null;
}

/**
 * What a fast day the person marked changes, for the day sheet and the planner alike. The deficit
 * is 0 for everyone — the research (plan §3.2) only insists on it at 60+ and at hypoglycaemia risk,
 * but a fast already is a deficit, and a budget that asked for 500 under Out on a vrat day would
 * be asking for two. What differs by person is whether a meal may go.
 */
export function fastDayRule(S: KayaState, today = new Date()): FastDayRule {
  const why = S.profile.eating?.hypoRisk === true ? "low sugar" : older(S, today) ? "60+" : null;
  return { deficit: 0, keepMeals: noSkippedMeals(S, today), why };
}

/* ---------- protein ---------- */

function storedProtein(S: KayaState): number | null {
  const n = S.profile.proteinPerKg;
  return typeof n === "number" && Number.isFinite(n) && n > 0 ? n : null;
}

export function defaultProteinPerKg(S: KayaState, today = new Date()): number {
  return older(S, today) ? OLDER_PROTEIN_PER_KG.default : DEFAULT_PROTEIN_PER_KG;
}

export function proteinSettingRange(S: KayaState, today = new Date()): { min: number; max: number } {
  return older(S, today) ? { min: OLDER_PROTEIN_PER_KG.min, max: OLDER_PROTEIN_PER_KG.max } : { min: 0.5, max: 3 };
}

/** What the settings box shows: the saved rate (or the age's default), inside the age's range —
 *  a 1.6 saved at 45 reads as 1.2 once the person is 60. */
export function proteinSetting(S: KayaState, today = new Date()): number {
  const { min, max } = proteinSettingRange(S, today);
  const n = storedProtein(S) ?? defaultProteinPerKg(S, today);
  return Math.round(Math.min(max, Math.max(min, n)) * 100) / 100;
}

export interface ProteinRule {
  perKg: number;
  /** grams a meal: the floor a plan aims every main meal at */
  mealG: number;
  /** "kidney" holds protein down; "glp1" holds it up; "60+" narrows the range */
  reason: "kidney" | "glp1" | "60+" | null;
}

export function proteinRule(S: KayaState, today = new Date()): ProteinRule {
  const isOlder = older(S, today);
  const mealG = isOlder ? MEAL_PROTEIN_FLOOR_G.older : MEAL_PROTEIN_FLOOR_G.adult;
  const set = proteinSetting(S, today);
  // Kidney disease outranks the medicine's floor: raising protein is the one thing the doctor
  // has to decide there.
  if (hasCondition(S, "kidney"))
    return { perKg: Math.min(set, KIDNEY_PROTEIN_PER_KG), mealG, reason: "kidney" };
  if (takesMed(S, "glp1") && set < GLP1_PROTEIN_PER_KG)
    return { perKg: GLP1_PROTEIN_PER_KG, mealG, reason: "glp1" };
  return { perKg: set, mealG, reason: isOlder ? "60+" : null };
}

/* ---------- iron ---------- */

export interface IronRule {
  mg: number | null;
  reason: "periods stopped" | "50+" | null;
}

export function ironRule(S: KayaState, today = new Date()): IronRule {
  const sex = S.profile.sex;
  if (sex === "male") return { mg: IRON_MG.male, reason: null };
  if (sex !== "female") return { mg: null, reason: null };
  if (periodsStopped(S)) return { mg: IRON_MG.male, reason: "periods stopped" };
  const a = age(S, today);
  if (a !== null && a >= IRON_ADULT_AGE) return { mg: IRON_MG.male, reason: "50+" };
  return { mg: IRON_MG.female, reason: null };
}

/* ---------- the lines ---------- */

const n = (v: number) => v.toLocaleString();
const g = (v: number) => (Math.round(v * 100) / 100).toString();

/**
 * One sentence per rule that applies to this person, in the order the targets are listed. Every
 * line names its rule ("Iron 19 mg: periods have stopped …"), so a reader can tell a default from
 * an adjustment. Empty for an adult with no answers: the page's general paragraph covers them.
 */
export function eatingEvidence(S: KayaState, today = new Date(), device?: DeviceAnswers): string[] {
  const out: string[] = [];
  const d = deficitRule(S, today, device);
  const p = proteinRule(S, today);
  const iron = ironRule(S, today);
  const isOlder = older(S, today);
  const preg = pregnancy(S);

  if (preg)
    out.push(
      `Deficit 0: ${preg === "pregnant" ? "pregnant" : "breastfeeding"} — the body needs more, not less, and a doctor sets these targets.`
    );
  if (d.limits.includes("gentle"))
    out.push(
      "Deficit 0 on this phone: you asked Kaya to go gentler. It stays off until you change that answer under You › Food & calories."
    );
  if (d.limits.includes("60+"))
    out.push(
      `After 60 the deficit stays moderate — never more than ${n(OLDER_MAX_DEFICIT_KCAL)} kcal a day — because eating far less than you burn takes muscle as well as fat.${d.capped && !preg ? ` ${n(d.asked)} was asked for, so ${n(d.kcal)} is used.` : ""}`
    );
  // After 60 the age line already says 500; a second line for the same cap would only repeat it.
  if (d.limits.includes("diabetes") && !d.limits.includes("60+"))
    out.push(
      `Deficit at most ${n(DIABETES_MAX_DEFICIT_KCAL)} kcal: diabetes — a bigger gap makes blood sugar harder to hold steady.${d.capped ? ` ${n(d.asked)} was asked for, so ${n(d.kcal)} is used.` : ""}`
    );

  // After 60 or with diabetes the 500 is already said; the medicine's line then only adds why.
  if (d.limits.includes("glp1")) {
    const saidAlready = d.limits.includes("60+") || d.limits.includes("diabetes");
    out.push(
      `Deficit never above ${n(GLP1_MAX_DEFICIT_KCAL)} kcal on a GLP-1 or weight-loss medicine: appetite is already low, and a bigger gap takes muscle. Your doctor sets the dose.${d.capped && !saidAlready ? ` ${n(d.asked)} was asked for, so ${n(d.kcal)} is used.` : ""}`
    );
  }

  if (p.reason === "kidney")
    out.push(
      `Protein ${g(p.perKg)} g per kg: kidney disease — Kaya does not raise it above the adult allowance. Your doctor sets this.`
    );
  else if (p.reason === "glp1")
    out.push(
      `Protein at least ${g(GLP1_PROTEIN_PER_KG)} g per kg on a GLP-1 or weight-loss medicine, in smaller meals: it keeps the muscle while the weight comes off.`
    );
  else if (p.reason === "60+")
    out.push(
      `Protein rises to ${g(p.perKg)} g per kg and is spread across the day, about ${p.mealG} g at each meal; the body keeps more of it that way than from one large dinner.`
    );

  if (iron.reason === "periods stopped")
    out.push(`Iron ${iron.mg} mg: periods have stopped — 29 mg is for the menstruating years.`);
  else if (iron.reason === "50+")
    out.push(`Iron ${iron.mg} mg: after 50 — 29 mg is for the menstruating years.`);

  if (isOlder)
    out.push(`Calcium ${n(1000)} mg, the adult figure — a higher one for 60+ is not yet confirmed.`);

  if (S.profile.eating?.hypoRisk === true)
    out.push("A plan never skips a meal: your tablets can bring sugar too low when a meal is missed.");
  else if (isOlder) out.push("A plan never skips a meal.");

  if (isOlder) {
    const sex = S.profile.sex;
    const litres =
      sex === "male" || sex === "female"
        ? `${HYDRATION_L[sex].toFixed(1)} L`
        : `${HYDRATION_L.female.toFixed(1)}–${HYDRATION_L.male.toFixed(1)} L`;
    out.push(`Thirst fades after 60: drink at least ${litres} a day unless your doctor has set a limit.`);
  }
  return out;
}

/* ---------- protein by meal ---------- */

export interface MealProtein {
  meal: "breakfast" | "lunch" | "dinner";
  /** null when nothing is logged for the meal yet — not eaten is not the same as no protein */
  g: number | null;
  short: boolean;
}

const MAIN_MEALS: MealProtein["meal"][] = ["breakfast", "lunch", "dinner"];

/**
 * Each main meal's protein against the per-meal floor. The snack is left out: the floor is about
 * spreading protein over the meals that carry it, and the research's 25 g is a meal's figure.
 */
export function mealProtein(entries: FoodEntry[], floorG: number): MealProtein[] {
  return MAIN_MEALS.map((meal) => {
    const mine = entries.filter((e) => e.meal === meal);
    if (!mine.length) return { meal, g: null, short: false };
    const g = Math.round(mine.reduce((a, e) => a + (e.p || 0), 0));
    // Within a gram reads as met: the portions are estimates, and a 24 g lunch is not a problem
    // to send someone off to fix.
    return { meal, g, short: g < floorG - 1 };
  });
}

const MEAL_WORD: Record<MealProtein["meal"], string> = { breakfast: "Breakfast", lunch: "Lunch", dinner: "Dinner" };

/** The meals that ran short, in one sentence, or null when none did. */
export function mealProteinLine(rows: MealProtein[], floorG: number): string | null {
  const short = rows.filter((r) => r.short && r.g !== null);
  if (!short.length) return null;
  if (short.length === 1) return `${MEAL_WORD[short[0].meal]} ran short — ${short[0].g} of ${floorG} g.`;
  const names = short.map((r, i) => (i === 0 ? MEAL_WORD[r.meal] : MEAL_WORD[r.meal].toLowerCase()));
  const figs = short.map((r) => `${r.g}`);
  const join = (xs: string[]) => (xs.length === 2 ? xs.join(" and ") : `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`);
  return `${join(names)} ran short of ${floorG} g — ${join(figs)} g.`;
}
